// ============================================
// PÁGINA: Análise Geral de Paciente
// ============================================
// Dashboard com filtros, gráficos e análises de IA
// Criado em Nov/2025
// Atualizado Nov/2025: Novos gráficos, seletor de tipo, busca textual
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import GraficoSintomas from '../components/analise/GraficoSintomas';
import GraficoQueixas from '../components/analise/GraficoQueixas';
import GraficoHumor from '../components/analise/GraficoHumor';
import GraficoIntensidade from '../components/analise/GraficoIntensidade';
import GraficoMedicamentos from '../components/analise/GraficoMedicamentos';
import BuscaTextualProntuarios from '../components/analise/BuscaTextualProntuarios';
import PainelAnalisesIA from '../components/analise/PainelAnalisesIA';
import ComparadorProntuarios from '../components/analise/ComparadorProntuarios';

interface Paciente {
  id: number;
  nome: string;
  data_nascimento: string;
  genero: string;
}

interface DadosAgregados {
  paciente: Paciente;
  sintomas: Record<string, any>;
  queixas: Record<string, any>;
  humor: Record<string, any>;
  medicamentos: any[];
  evolucaoTemporal: any[];
  estatisticas: {
    totalProntuarios: number;
    totalConsultas: number;
    frequenciaMediaDias: number;
    periodoTotalDias: number;
    primeiraSessao: string;
    ultimaSessao: string;
  };
  prontuarios: any[];
}

export default function AnaliseGeral() {
  const navigate = useNavigate();

  // Estados principais
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState<number | null>(null);
  const [dadosAgregados, setDadosAgregados] = useState<DadosAgregados | null>(null);
  const [analisesIA, setAnalisesIA] = useState<any[]>([]);

  // Estados de filtros
  const [tipoFiltro, setTipoFiltro] = useState<'periodo' | 'ultimasSessoes'>('periodo');
  const [dataInicio, setDataInicio] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split('T')[0]);
  const [ultimasSessoes, setUltimasSessoes] = useState<number>(10);
  const [modeloIA, setModeloIA] = useState<string>('gemini-3-flash-preview');

  // Estados de carregamento
  const [loadingPacientes, setLoadingPacientes] = useState(true);
  const [loadingDados, setLoadingDados] = useState(false);
  const [gerandoIA, setGerandoIA] = useState(false);

  // Estado do comparador de prontuários
  const [mostrarComparador, setMostrarComparador] = useState(false);

  // ========================================
  // CARREGAR PACIENTES AO MONTAR
  // ========================================
  useEffect(() => {
    carregarPacientes();
  }, []);

  // ========================================
  // CARREGAR DADOS QUANDO PACIENTE MUDAR
  // ========================================
  useEffect(() => {
    if (pacienteSelecionado) {
      carregarDadosAgregados();
      carregarAnalisesIA();
    }
  }, [pacienteSelecionado, tipoFiltro, dataInicio, dataFim, ultimasSessoes]);

  // ========================================
  // CARREGAR LISTA DE PACIENTES
  // ========================================
  const carregarPacientes = async () => {
    try {
      setLoadingPacientes(true);
      const response = await api.get('/pacientes');
      
      if (response.data && Array.isArray(response.data)) {
        // Ordenar por nome
        const pacientesOrdenados = response.data.sort((a: Paciente, b: Paciente) =>
          a.nome.localeCompare(b.nome)
        );
        setPacientes(pacientesOrdenados);
      }
    } catch (error: any) {
      console.error('[ANALISE-GERAL] Erro ao carregar pacientes:', error);
      alert('Erro ao carregar lista de pacientes');
    } finally {
      setLoadingPacientes(false);
    }
  };

  // ========================================
  // CARREGAR DADOS AGREGADOS
  // ========================================
  const carregarDadosAgregados = async () => {
    if (!pacienteSelecionado) return;

    try {
      setLoadingDados(true);

      const periodo = tipoFiltro === 'periodo'
        ? { dataInicio, dataFim }
        : { ultimasSessoes };

      const response = await api.post('/analise-paciente/dados-agregados', {
        pacienteId: pacienteSelecionado,
        periodo
      });

      if (response.data.sucesso) {
        setDadosAgregados(response.data.dados);
        console.log('[ANALISE-GERAL] Dados agregados carregados:', response.data.dados.estatisticas);
      } else {
        throw new Error(response.data.mensagem || 'Erro ao carregar dados');
      }
    } catch (error: any) {
      console.error('[ANALISE-GERAL] Erro ao carregar dados agregados:', error);
      
      if (error.response?.status === 404) {
        alert('Nenhum prontuário encontrado para o período selecionado');
      } else {
        alert('Erro ao carregar dados: ' + (error.response?.data?.mensagem || error.message));
      }
      
      setDadosAgregados(null);
    } finally {
      setLoadingDados(false);
    }
  };

  // ========================================
  // CARREGAR ANÁLISES DE IA
  // ========================================
  const carregarAnalisesIA = async () => {
    if (!pacienteSelecionado) return;

    try {
      const response = await api.get(`/analise-paciente/listar/${pacienteSelecionado}`, {
        params: { limite: 10 }
      });

      if (response.data.sucesso) {
        setAnalisesIA(response.data.analises || []);
      }
    } catch (error: any) {
      console.error('[ANALISE-GERAL] Erro ao carregar análises:', error);
    }
  };

  // ========================================
  // GERAR INSIGHT COM IA
  // ========================================
  const handleGerarInsight = async () => {
    if (!dadosAgregados || !pacienteSelecionado) {
      alert('Selecione um paciente e carregue os dados primeiro');
      return;
    }

    try {
      setGerandoIA(true);

      const filtrosAplicados = tipoFiltro === 'periodo'
        ? { periodo: { dataInicio, dataFim }, tipo: 'periodo' }
        : { periodo: { ultimasSessoes }, tipo: 'ultimasSessoes' };

      const response = await api.post('/analise-paciente/gerar-insight', {
        pacienteId: pacienteSelecionado,
        dadosAgregados,
        filtrosAplicados,
        modeloIA
      });

      if (response.data.sucesso) {
        alert('✅ Análise gerada com sucesso!');
        // Recarregar análises
        await carregarAnalisesIA();
      } else {
        throw new Error(response.data.mensagem || 'Erro ao gerar análise');
      }
    } catch (error: any) {
      console.error('[ANALISE-GERAL] Erro ao gerar insight:', error);
      alert('Erro ao gerar análise: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      setGerandoIA(false);
    }
  };

  // ========================================
  // GERAR ANÁLISE DE EVOLUÇÃO
  // ========================================
  const handleGerarEvolucao = async () => {
    if (!dadosAgregados || !pacienteSelecionado) {
      alert('Selecione um paciente e carregue os dados primeiro');
      return;
    }

    try {
      setGerandoIA(true);

      const filtrosAplicados = tipoFiltro === 'periodo'
        ? { periodo: { dataInicio, dataFim }, tipo: 'periodo' }
        : { periodo: { ultimasSessoes }, tipo: 'ultimasSessoes' };

      const response = await api.post('/analise-paciente/gerar-evolucao', {
        pacienteId: pacienteSelecionado,
        dadosAgregados,
        filtrosAplicados,
        modeloIA
      });

      if (response.data.sucesso) {
        alert('✅ Análise de evolução gerada com sucesso!');
        await carregarAnalisesIA();
      } else {
        throw new Error(response.data.mensagem || 'Erro ao gerar análise');
      }
    } catch (error: any) {
      console.error('[ANALISE-GERAL] Erro ao gerar análise de evolução:', error);
      alert('Erro ao gerar análise: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      setGerandoIA(false);
    }
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">
                <i className="fas fa-chart-line me-2"></i>
                Análise Geral de Paciente
              </h1>
            </div>
            <div className="col-sm-6">
              <button
                className="btn btn-secondary float-end"
                onClick={() => navigate('/dashboard')}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          
          {/* CARD: Seletor de Paciente e Filtros */}
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter me-2"></i>
                Seleção e Filtros
              </h3>
            </div>
            <div className="card-body">
              {/* Linha 1: Seletor de Paciente */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label fw-bold" htmlFor="analiseGeralPaciente">
                    <i className="fas fa-user me-2"></i>
                    Selecione o Paciente
                  </label>
                  <select
                    id="analiseGeralPaciente"
                    className="form-select"
                    value={pacienteSelecionado || ''}
                    onChange={(e) => setPacienteSelecionado(parseInt(e.target.value))}
                    disabled={loadingPacientes}
                  >
                    <option value="">-- Selecione um paciente --</option>
                    {pacientes.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Linha 2: Tipo de Filtro */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label fw-bold">
                    <i className="fas fa-sliders-h me-2"></i>
                    Tipo de Filtro
                  </label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${tipoFiltro === 'periodo' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setTipoFiltro('periodo')}
                    >
                      <i className="fas fa-calendar-alt me-2"></i>
                      Por Período de Datas
                    </button>
                    <button
                      type="button"
                      className={`btn ${tipoFiltro === 'ultimasSessoes' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setTipoFiltro('ultimasSessoes')}
                    >
                      <i className="fas fa-history me-2"></i>
                      Últimas Sessões
                    </button>
                  </div>
                </div>
              </div>

              {/* Linha 3: Filtros Específicos */}
              {tipoFiltro === 'periodo' && (
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="analiseGeralDataInicio">Data Início</label>
                    <input
                      id="analiseGeralDataInicio"
                      type="date"
                      className="form-control"
                      value={dataInicio}
                      onChange={(e) => setDataInicio(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="analiseGeralDataFim">Data Fim</label>
                    <input
                      id="analiseGeralDataFim"
                      type="date"
                      className="form-control"
                      value={dataFim}
                      onChange={(e) => setDataFim(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {tipoFiltro === 'ultimasSessoes' && (
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="analiseGeralUltimasSessoes">Número de Sessões</label>
                    <input
                      id="analiseGeralUltimasSessoes"
                      type="number"
                      className="form-control"
                      min="1"
                      max="100"
                      value={ultimasSessoes}
                      onChange={(e) => setUltimasSessoes(parseInt(e.target.value) || 10)}
                    />
                    <small className="form-text text-muted">
                      Exibir as últimas N sessões do paciente
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* LOADING */}
          {loadingDados && (
            <div className="alert alert-info">
              <i className="fas fa-spinner fa-spin me-2"></i>
              Carregando dados...
            </div>
          )}

          {/* DADOS CARREGADOS */}
          {dadosAgregados && !loadingDados && (
            <>
              {/* Card: Estatísticas Gerais */}
              <div className="row">
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-info">
                    <div className="inner">
                      <h3>{dadosAgregados.estatisticas.totalProntuarios}</h3>
                      <p>Total de Sessões</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-file-medical"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-success">
                    <div className="inner">
                      <h3>{Math.round(dadosAgregados.estatisticas.periodoTotalDias / 30)}</h3>
                      <p>Meses de Tratamento</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-calendar-check"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-warning">
                    <div className="inner">
                      <h3>{dadosAgregados.estatisticas.frequenciaMediaDias.toFixed(1)}</h3>
                      <p>Dias Entre Sessões</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-clock"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-danger">
                    <div className="inner">
                      <h3>{Object.keys(dadosAgregados.sintomas).length}</h3>
                      <p>Sintomas Diferentes</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-heartbeat"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card: Comparador de Prontuários */}
              <div className="card card-primary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-columns me-2"></i>
                    Visualização e Comparação de Prontuários
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12 mb-2">
                      <button
                        className="btn btn-primary btn-lg w-100"
                        onClick={() => setMostrarComparador(true)}
                        disabled={loadingDados || !dadosAgregados}
                      >
                        <i className="fas fa-file-medical me-2"></i>
                        Visualizar e Comparar Prontuários
                      </button>
                      <small className="form-text text-muted d-block mt-1">
                        Visualize até 2 prontuários lado a lado para comparação detalhada. 
                        O último prontuário será carregado automaticamente.
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card: Botões de IA */}
              <div className="card card-success card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-brain me-2"></i>
                    Análises com Inteligência Artificial
                  </h3>
                </div>
                <div className="card-body">
                  {/* Seletor de Modelo */}
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <div className="alert alert-info">
                        <label className="form-label fw-bold" htmlFor="modeloIA">
                          <i className="fas fa-robot me-2"></i>
                          Modelo de IA
                        </label>
                        <select
                          id="modeloIA"
                          className="form-select"
                          value={modeloIA}
                          onChange={(e) => setModeloIA(e.target.value)}
                          disabled={gerandoIA}
                        >
                          <option value="gemini-3-flash-preview">Sophya 3 Flash Preview (Recomendado)</option>
                          <option value="gemini-2.5-flash">Sophya 2.5 Flash (Rápido)</option>
                          <option value="gemini-2.5-flash-lite">Sophya 2.5 Lite (Mais Leve)</option>
                        </select>
                        <small className="form-text text-muted">
                          Escolha o modelo para testar diferentes respostas. Preview = mais recente, Flash = rápido, Lite = leve.
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <button
                        className="btn btn-success btn-lg w-100"
                        onClick={handleGerarInsight}
                        disabled={gerandoIA || loadingDados}
                      >
                        {gerandoIA ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processando IA... (aguarde até 1 minuto)
                          </>
                        ) : (
                          <>
                            <i className="fas fa-robot me-2"></i>
                            Gerar Insight Geral
                          </>
                        )}
                      </button>
                      <small className="form-text text-muted d-block mt-1">
                        Análise completa dos padrões identificados nos dados. Tempo: 30-60s
                      </small>
                    </div>
                    <div className="col-md-6 mb-2">
                      <button
                        className="btn btn-info btn-lg w-100"
                        onClick={handleGerarEvolucao}
                        disabled={gerandoIA || loadingDados}
                      >
                        {gerandoIA ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processando IA... (aguarde até 1 minuto)
                          </>
                        ) : (
                          <>
                            <i className="fas fa-chart-line me-2"></i>
                            Analisar Evolução de Sintomas
                          </>
                        )}
                      </button>
                      <small className="form-text text-muted d-block mt-1">
                        Análise temporal da evolução clínica. Tempo: 30-60s
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Busca Textual em Prontuários */}
              <BuscaTextualProntuarios 
                prontuarios={dadosAgregados.prontuarios}
                onVerProntuario={(prontuarioId) => {
                  setMostrarComparador(true);
                  console.log('[ANALISE-GERAL] Ver prontuário:', prontuarioId);
                }}
              />

              {/* Gráficos - Linha 1: Evolução Temporal e Medicamentos */}
              <div className="row">
                <div className="col-lg-8">
                  <GraficoIntensidade dados={dadosAgregados.evolucaoTemporal} />
                </div>
                <div className="col-lg-4">
                  <GraficoMedicamentos dados={dadosAgregados.medicamentos} />
                </div>
              </div>

              {/* Gráficos - Linha 2: Sintomas e Queixas */}
              <div className="row">
                <div className="col-md-6">
                  <GraficoSintomas dados={dadosAgregados.sintomas} />
                </div>
                <div className="col-md-6">
                  <GraficoQueixas dados={dadosAgregados.queixas} />
                </div>
              </div>

              {/* Gráficos - Linha 3: Humor */}
              <div className="row">
                <div className="col-md-12">
                  <GraficoHumor dados={dadosAgregados.humor} />
                </div>
              </div>

              {/* Painel de Análises IA */}
              <PainelAnalisesIA 
                analises={analisesIA}
                onRecarregar={carregarAnalisesIA}
                pacienteNome={dadosAgregados.paciente.nome}
              />
            </>
          )}

          {/* NENHUM PACIENTE SELECIONADO */}
          {!pacienteSelecionado && !loadingDados && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Selecione um paciente para começar a análise
            </div>
          )}
        </div>
      </div>

      {/* Modal: Comparador de Prontuários */}
      {dadosAgregados && dadosAgregados.prontuarios && (
        <ComparadorProntuarios
          show={mostrarComparador}
          onClose={() => setMostrarComparador(false)}
          pacienteNome={dadosAgregados.paciente.nome}
          prontuarios={dadosAgregados.prontuarios}
        />
      )}
    </>
  );
}
